import paper from 'paper';


/**
 *  Paper Wave Effect
 *  --------------------------------------------
 *  Tworzy organiczną elastyczną falę reagującą na ruch kursora.
 *
 *  Parametry:
 *  @param {string} canvasSelector - selektor elementu <canvas>, na którym rysowana jest animacja
 *
 *  Zachowanie:
 *    - Fala animuje się tylko wtedy, gdy sekcja z canvasem jest widoczna w viewportcie
 *    - Po opuszczeniu widoku animacja jest wstrzymywana, aby nie obciążać CPU
 *    - Reaguje na rozmiar okna i ruch kursora
 */



export function initPaperWave(canvasSelector, options = {}) {
  const canvas = document.querySelector(canvasSelector);
  if (!canvas) return console.error(`Nie znaleziono canvasa: ${canvasSelector}`);

  // Inicjalizacja Paper.js
  paper.setup(canvas);
  const { view, Path, Point } = paper;

  const values = {
    friction: 0.8,
    timeStep: 0.01,
    amount: 15,
    mass: 2,
    invMass: 1 / 2
  };

  let path;
  let springs = [];
  let size = view.size.multiply([1.2, 1]);

  // Klasa Spring
  class Spring {
    constructor(a, b, strength, restLength) {
      this.a = a;
      this.b = b;
      this.restLength = restLength || 80;
      this.strength = strength || 0.55;
      this.mamb = values.invMass * values.invMass;
    }
    update() {
      const delta = this.b.subtract(this.a);
      const dist = delta.length;
      const normDistStrength = (dist - this.restLength) / (dist * this.mamb) * this.strength;
      delta.y *= normDistStrength * values.invMass * 0.2;
      if (!this.a.fixed) this.a.y += delta.y;
      if (!this.b.fixed) this.b.y -= delta.y;
    }
  }

  function createPath(strength) {
    const path = new Path({ fillColor: 'black' });
    springs = [];

    for (let i = 0; i <= values.amount; i++) {
      const segment = path.add(new Point(i / values.amount, 0.5).multiply(size));
      const point = segment.point;
      if (i === 0 || i === values.amount) point.y += size.height;

      point.px = point.x;
      point.py = point.y;
      point.fixed = i < 2 || i > values.amount - 2;

      if (i > 0) springs.push(new Spring(segment.previous.point, point, strength));
    }

    path.position.x -= size.width / 4;
    return path;
  }

  function updateWave(path) {
    const force = 1 - values.friction * values.timeStep * values.timeStep;

    for (const seg of path.segments) {
      const point = seg.point;
      const dy = (point.y - point.py) * force;
      point.py = point.y;
      point.y = Math.max(point.y + dy, 0);
    }

    for (const s of springs) s.update();
    path.smooth({ type: 'continuous' });
  }

  function resize() {
    if (path) path.remove();
    size = view.bounds.size.multiply([2, 1]);
    path = createPath(0.1);
  }

  function mouseMove(event) {
    const location = path.getNearestLocation(event.point);
    const segment = location.segment;
    const point = segment.point;

    if (!point.fixed && location.distance < size.height / 4) {
      const y = event.point.y;
      point.y += (y - point.y) / 6;
      if (segment.previous && !segment.previous.fixed) {
        const prev = segment.previous.point;
        prev.y += (y - prev.y) / 24;
      }
      if (segment.next && !segment.next.fixed) {
        const next = segment.next.point;
        next.y += (y - next.y) / 24;
      }
    }
  }

  // Eventy Paper.js
  view.onResize = resize;
  view.onMouseMove = mouseMove;
  view.onFrame = () => updateWave(path);

  resize();

  // Opcjonalnie (options.reveal = selektor elementu pod falą):
  // element jest klikalny dopiero tam, gdzie fala go odsłoniła. Canvas dalej odbiera
  // ruch myszy (potrzebny fali), więc o odsłonięciu decyduje przezroczystość piksela canvasa.
  if (options.reveal) {
    const ctx = canvas.getContext('2d');

    const revealedTargetAt = (clientX, clientY) => {
      const el = document.querySelector(options.reveal);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      if (clientX < r.left || clientX > r.right || clientY < r.top || clientY > r.bottom) return null;

      const c = canvas.getBoundingClientRect();
      const px = Math.floor((clientX - c.left) * canvas.width / c.width);
      const py = Math.floor((clientY - c.top) * canvas.height / c.height);
      const covered = ctx.getImageData(px, py, 1, 1).data[3] > 20;
      return covered ? null : el;
    };

    canvas.addEventListener('mousemove', (e) => {
      canvas.style.cursor = revealedTargetAt(e.clientX, e.clientY) ? 'pointer' : '';
    });

    canvas.addEventListener('click', (e) => {
      const el = revealedTargetAt(e.clientX, e.clientY);
      if (el) (el.closest('a') || el).click();
    });
  }
}
